import { FilesetResolver, PoseLandmarker } from "@mediapipe/tasks-vision";

import { Camera, isMobile } from "./camera";
import { LandmarkSmoother } from "./one_euro_filter";
import { RendererCanvas2d } from "./renderer_canvas2d";
import {
  MODEL_ASSETS,
  POSE_LANDMARK_NAMES,
  STATE,
  VIDEO_SIZE,
  WASM_PATH,
} from "./params";
import { FrameBuffer } from "./metrics/frame_buffer";
import { buildSample } from "./metrics/sample";
import { evaluateMetrics, VIEWS } from "./metrics/definitions";
import { Readout } from "./metrics/readout";
import { SessionRecorder, downloadSession } from "./metrics/recorder";
import { RecorderControls } from "./metrics/controls";
import { CoachControls } from "./metrics/coach";
import { PostureTally, footScale } from "./metrics/posture";
import { SetScore } from "./metrics/score";
import { Progress } from "./metrics/progress";
import { WorkoutStore } from "./metrics/history";
import { Receipt } from "./metrics/receipt";
import {
  DEFAULT_EXERCISE,
  EXERCISES,
  exerciseById,
  metricsFor,
} from "./exercises";
import { ExerciseMenu } from "./exercises/menu";
import { nearSide } from "./metrics/sample";
import { VideoFile } from "./video_source";
import { Intro } from "./intro";
import {
  Startup,
  fetchWithProgress,
  formatMB,
  trackFetchProgress,
} from "./startup";

let source, landmarker, renderer, rafId;
/** Whether a frame has already thrown, so the loop reports it once. */
let loopFailed = false;
/** Whether a `play()` is already in flight, so the loop does not stack them. */
let resuming = false;
/**
 * The camera watchdog.
 *
 * `resumeIfPaused` covers the element being paused, which is one of the two
 * ways iOS takes the picture away. The other leaves the element playing and
 * stops the frames: the track is interrupted — by the download sheet a saved
 * recording opens, by the lock button, by another app taking the camera — and
 * comes back muted or ended. `paused` is false, `readyState` is 4, and
 * `currentTime` simply never advances again, so the loop never re-infers and
 * goes on drawing the last frame it had. That is the freeze the athlete kept
 * reporting after the pause fix shipped.
 *
 * Nothing recovers a dead track but asking for a new one, so a camera that has
 * produced no frame for `STALL_RESTART_MS` while the page is on screen is torn
 * down and set up again. The backoff doubles the wait after each restart that
 * does not produce frames, up to `STALL_RESTART_MAX_MS`: a phone that has
 * genuinely lost the camera — permission revoked, another app holding it — must
 * not be asked for it twice a second for as long as the tab is open.
 */
const STALL_RESTART_MS = 2000;
const STALL_RESTART_MAX_MS = 30000;
let lastFrameAt = 0;
let restarting = false;
let restarts = 0;
let normalizedSmoother, worldSmoother;
let frames, readout, recorder, controls, coach, startup;
/** Set the first time ?record=1 arms the recorder, so it arms exactly once. */
let autoRecordArmed = false;
let reps, posture;
/**
 * The drill being scored, from the menu or `?exercise=`. Everything that is
 * specific to one movement — how a rep is counted, which criteria are checked,
 * what to say when one fails — hangs off this; see exercises/index.js.
 */
let exercise = null;
let menu, receipt, workout, set, progress, history;
/**
 * What each banked set earned, in the order the workout holds them, so the
 * ticket can print the XP and any level crossed beside the set that did it.
 */
let earned;
// detectForVideo requires strictly increasing timestamps, and re-running
// inference on a frame the source has not replaced yet is wasted work.
let lastVideoTime = -1;
let lastTimestamp = -1;
let inferenceTime = 0;
let lastResult = null;
/**
 * Foot length for the tiptoe criterion, measured over the session rather than
 * per rep. An occluded foot measures a third of its real length, and a per-rep
 * scale would read that as a raised heel.
 */
let sessionFootScale = null;

/**
 * Wraps a Camera in the same shape VideoFile has, so the render loop does not
 * branch on which it has. The two differ in exactly two ways: a camera preview
 * is mirrored and a file is not, and a camera runs on wall-clock time while a
 * file runs on its own.
 */
function cameraSource(camera) {
  return {
    video: camera.video,
    mirrored: true,
    timestamp: () => performance.now(),
    // A paused camera is always an accident; a paused file is the review
    // control doing its job. See `resumeIfPaused`.
    keepPlaying: true,
  };
}

async function createLandmarker() {
  // Resolving the fileset only computes paths; the ~11MB runtime binary is
  // fetched later, inside createFromOptions.
  const vision = await FilesetResolver.forVisionTasks(WASM_PATH);

  // Fetched here rather than handed to MediaPipe as a path, so the download —
  // by far the longest part of startup, and megabytes on a phone — can report
  // real progress instead of an opaque wait.
  startup.stage(
    "Loading pose model\u2026",
    `${STATE.model} model, cached after the first visit`
  );
  const modelBytes = await fetchWithProgress(
    MODEL_ASSETS[STATE.model],
    (fraction, received, total) => {
      startup.progress(
        fraction,
        total
          ? `${formatMB(received)} of ${formatMB(total)}`
          : `${formatMB(received)} downloaded`
      );
    }
  );

  // The runtime download happens inside createFromOptions, so its progress has
  // to be observed rather than driven.
  startup.stage(
    "Loading pose runtime\u2026",
    "about 11MB, cached after the first visit"
  );
  const untrack = trackFetchProgress(
    "vision_wasm",
    (fraction, received, total) => {
      startup.progress(
        fraction,
        total
          ? `${formatMB(received)} of ${formatMB(total)}`
          : `${formatMB(received)} downloaded`
      );
    }
  );

  try {
    return await PoseLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetBuffer: modelBytes,
        // GPU keeps a heavier model real-time; MediaPipe falls back to CPU on
        // its own if the delegate is unavailable.
        delegate: "GPU",
      },
      // VIDEO mode carries tracking state between frames, which both steadies
      // the landmarks and avoids re-running whole-frame detection every time.
      runningMode: "VIDEO",
      ...STATE.landmarker,
    });
  } finally {
    untrack();
  }
}

/**
 * Smoothing must be applied per pose, and the filters hold per-landmark state,
 * so this only supports a single tracked athlete. numPoses > 1 would need a
 * smoother bank keyed by a stable track id, which the task does not expose.
 */
function smooth(result, timestamp) {
  if (result.landmarks.length === 0) {
    normalizedSmoother.reset();
    worldSmoother.reset();
    return result;
  }
  if (!STATE.smoothing.enabled) {
    return result;
  }
  return {
    ...result,
    landmarks: [normalizedSmoother.smooth(result.landmarks[0], timestamp)],
    worldLandmarks:
      result.worldLandmarks.length > 0
        ? [worldSmoother.smooth(result.worldLandmarks[0], timestamp)]
        : result.worldLandmarks,
  };
}

/**
 * Monotonic timestamps for `detectForVideo`, which rejects one it has already
 * seen, from a source clock that is not guaranteed to only move forwards.
 *
 * Scrubbing a file back to watch a rep again rewinds `currentTime`, and feeding
 * that in would make the detector refuse every frame until playback passed the
 * furthest point already reached — the overlay would freeze for exactly the
 * stretch being reviewed. So inference gets its own ever-increasing clock, while
 * the metrics and the recorder keep the source's real timestamp, which for a
 * file is media time and is the honest one to record.
 */
function inferenceClock(timestamp) {
  const step = timestamp > lastTimestamp ? timestamp - lastTimestamp : 1;
  lastTimestamp = timestamp;
  inferenceTime += step;
  return inferenceTime;
}

/**
 * Keeps the camera's own `<video>` playing.
 *
 * Nothing in this app ever started it: a stream assigned to `srcObject`
 * autoplays, so there is no `play()` call to repeat. iOS pauses that element
 * whenever the system takes the screen — a download or share sheet, the lock
 * button, a tab switch — and when the page comes back nothing resumes it. The
 * loop then sees `currentTime` standing still, never re-infers, and goes on
 * drawing the last frame it had: a camera frozen for good while every button
 * still works. Saving a recording is the reliable way to produce it, because
 * the download sheet is exactly that kind of interruption, which is why this
 * was reported as "it freezes when I finish the set and save the take".
 *
 * A file is the opposite case — pausing it is the review control doing its job
 * — so only a source that asks for this gets it.
 *
 * `play()` rejects if it is called again while the element is still recovering,
 * so the rejection is swallowed and the next frame simply tries again.
 */
function resumeIfPaused(video) {
  if (!source.keepPlaying || !video.paused || resuming) {
    return;
  }
  resuming = true;
  const done = () => {
    resuming = false;
  };
  const attempt = video.play();
  if (attempt && typeof attempt.then === "function") {
    attempt.then(done, done);
  } else {
    done();
  }
}

/**
 * Lets go of the camera and asks for it again.
 *
 * The old tracks are stopped first and deliberately: a track that is muted
 * rather than ended still holds the device, and iOS will hand back the same
 * dead track rather than a fresh one while it does. `Camera.setup` reuses the
 * same `<video>` element, re-plays it and re-waits for real frames, so what
 * comes back is a working source or an error.
 */
async function restartCamera() {
  if (restarting || !source.keepPlaying) {
    return;
  }
  restarting = true;
  restarts += 1;
  try {
    const stream = source.video.srcObject;
    if (stream && typeof stream.getTracks === "function") {
      for (const track of stream.getTracks()) {
        track.stop();
      }
    }
    source.video.srcObject = null;
    source = cameraSource(await Camera.setup(STATE.camera));
    // The new stream starts its own clock, so the guard must not compare
    // against the dead one's last time.
    lastVideoTime = -1;
  } catch (error) {
    console.error("camera restart failed", error);
  } finally {
    restarting = false;
    // Whether it worked or not, the next window starts now: a failed restart
    // backs off rather than retrying on the very next frame.
    lastFrameAt = performance.now();
  }
}

/**
 * Restarts the camera if it has stopped producing frames.
 *
 * Only while the page is on screen. A backgrounded tab stops delivering frames
 * for perfectly good reasons, and `visibilitychange` resets the window on the
 * way back so a return from the lock screen gets its two seconds to resume by
 * itself before the camera is torn down.
 */
function watchForStall(now) {
  if (!source.keepPlaying || restarting) {
    return;
  }
  if (
    typeof document !== "undefined" &&
    document.visibilityState === "hidden"
  ) {
    lastFrameAt = now;
    return;
  }
  const wait = Math.min(
    STALL_RESTART_MS * Math.pow(2, restarts),
    STALL_RESTART_MAX_MS
  );
  if (now - lastFrameAt > wait) {
    restartCamera();
  }
}

function renderResult() {
  const video = source.video;
  resumeIfPaused(video);
  const now = performance.now();
  if (lastFrameAt === 0) {
    lastFrameAt = now;
  }
  if (video.readyState < 2) {
    watchForStall(now);
    return;
  }

  if (video.currentTime !== lastVideoTime) {
    lastVideoTime = video.currentTime;
    // A frame the source actually replaced, which is the only evidence that the
    // camera is alive: `paused` and `readyState` both lie about a dead track.
    lastFrameAt = now;
    restarts = 0;
    const timestamp = source.timestamp();
    const detected = landmarker.detectForVideo(
      video,
      inferenceClock(timestamp)
    );
    // Recorded before smoothing: filtering can be reapplied offline with any
    // parameters, but it cannot be removed after the fact.
    if (detected.worldLandmarks.length > 0) {
      if (STATE.metrics.autoRecord && !autoRecordArmed) {
        // Armed by ?record=1: begin at the first detected pose rather than on
        // load, so a tripod setup does not bank seconds of an empty gym. Armed
        // once and tracked here rather than read back off the recorder: during
        // the countdown the recorder has not started, so a recorder-derived
        // guard fires again on the next frame and the second call reads as the
        // Cancel it is, tearing the countdown down and starting it again sixty
        // times a second.
        autoRecordArmed = true;
        controls.arm();
      }
      recorder.capture(detected.worldLandmarks[0], timestamp);
    }
    lastResult = smooth(detected, timestamp);
    recordSample(lastResult, timestamp);
  } else {
    watchForStall(now);
  }

  renderer.draw(video, lastResult);
  drawMetrics();
}

/**
 * Metrics run off the smoothed world landmarks, so they inherit the same
 * filtering the overlay shows. Losing the pose clears the history rather than
 * letting a stale window blend across a gap.
 */
function recordSample(result, timestamp) {
  if (!STATE.metrics.enabled) {
    return;
  }
  if (result.worldLandmarks.length === 0) {
    frames.clear();
    return;
  }
  const sample = buildSample(result.worldLandmarks[0], timestamp);
  frames.push(sample);
  countRep(sample);
}

/**
 * Feeds the counter and scores each rep as it closes.
 *
 * The near side is taken from the recent window rather than from this frame: it
 * is a property of where the athlete stands relative to the camera, and letting
 * it flip mid-rep would swap which arm is being followed partway through a
 * revolution.
 */
function countRep(sample) {
  if (!scoringEnabled()) {
    return;
  }
  // Only during a set. Counting from page load would have the athlete walking
  // back from the phone on rep 1, and would make Start mean nothing.
  if (coach && !coach.running) {
    return;
  }
  const window = frames.window(STATE.metrics.windowMs);
  const side = window.length > 0 ? nearSide(window) : "left";
  const completed = reps.push(sample, side);
  // A hold reports every frame, not only when one ends: the seconds on the bar
  // and the sound that says the line went are both live, and by the time a hold
  // closes the athlete is already on the floor. A rep drill has no state and
  // this does nothing for it.
  if (coach && exercise.timed) {
    coach.showHoldState(reps.state, reps.seconds);
  }
  if (completed == null) {
    return;
  }
  scoreCompletedRep(completed, side, window);
}

/**
 * Delivers one completed rep: the verdict, the sound, the tally, the ticket.
 *
 * Separate from `countRep` because a rep can also arrive at the end of a set
 * rather than on a frame — see `flushRep`.
 */
function scoreCompletedRep(completed, side, window) {
  if (sessionFootScale == null && exercise.needsFootScale) {
    sessionFootScale = footScale(window, side);
  }
  // The exercise decides what a rep is judged on. Everything from here down —
  // the verdict, the sounds, the live tally, the receipt — reads only the row
  // shape, so a new drill needs no changes to any of it.
  const scored = exercise.scoreRep(completed, {
    side,
    thresholds: STATE.posture,
    footScale: sessionFootScale,
  });
  posture.add(scored);
  if (set) {
    set.add(scored);
  }
  if (coach) {
    coach.showRep(reps.count, scored);
  }
}

/**
 * Closes a rep whose counter is still waiting on frames, at the end of a set.
 *
 * A jump's rep window runs past the point the knee re-extends, so the counter
 * holds the last one open waiting either for the knee to come back down or for
 * its cap to run out. Both need frames, and frames stop mattering the moment
 * the athlete presses Stop. Without this the last jump of every set would be
 * missing from the count and from the ticket.
 */
function flushRep() {
  if (!scoringEnabled() || reps == null || typeof reps.flush !== "function") {
    return;
  }
  const completed = reps.flush();
  if (completed == null) {
    return;
  }
  const window = frames.window(STATE.metrics.windowMs);
  scoreCompletedRep(
    completed,
    window.length > 0 ? nearSide(window) : "left",
    window
  );
}

/**
 * Scoring needs a drill AND the camera view its criteria are honest in. The
 * menu sets both at once, so a mismatch only happens with `?view=` and
 * `?exercise=` set against each other, which is worth refusing rather than
 * answering with numbers measured in the wrong plane.
 */
function scoringEnabled() {
  return (
    STATE.posture.enabled &&
    exercise != null &&
    exercise.view === STATE.metrics.view
  );
}

/**
 * Points the app at a drill: the counter it needs, the view its criteria are
 * measured in, and the cues its bar says.
 */
function selectExercise(chosen) {
  exercise = chosen;
  STATE.metrics.view = chosen.view;
  // The counter gets the session's foot scale the same way `scoreRep` does, as
  // a getter rather than a value: it is not known until the first rep has been
  // seen, and a counter that measured it per rep would judge the same movement
  // differently from the criteria that score it.
  reps = chosen.createCounter(STATE.posture, {
    footScale: () => sessionFootScale,
  });
  posture.reset();
  sessionFootScale = null;
  document.getElementById("coach").hidden = false;
  if (coach) {
    coach.setExercise(chosen);
  }
  renderReceiptButton();
}

/** A set starts from zero: the tally is about this set, not the page's lifetime. */
function startSet() {
  reps.reset();
  posture.reset();
  sessionFootScale = null;
  set = new SetScore(exercise);
}

/**
 * Ends the set and shows the ticket for the workout so far.
 *
 * The receipt comes up on its own rather than behind a second button, because
 * stopping the set IS the athlete saying they are done with it, and a summary
 * nobody opens is not a summary. A set with no reps in it was a false start and
 * is dropped: it would otherwise print a line scored 0 of 0 and drag the total
 * down with it.
 */
function endSet() {
  if (set == null) {
    return;
  }
  // Before the set is closed, so the last rep is in it.
  flushRep();
  const finished = set.end();
  set = null;
  if (finished.reps === 0) {
    return;
  }
  // A set finished after midnight belongs to the new day, and starts its ticket.
  if (history.rolledOver()) {
    history.startNewDay();
    workout.reset();
    earned.length = 0;
  }
  workout.add(finished);
  // Banked before the ticket is drawn, so the line that says a level was
  // crossed and the bar on the menu behind it cannot disagree.
  earned.push(progress.add(finished));
  history.save(workout, earned);
  renderReceiptButton();
  receipt.show(workout, earned);
}

/** Back to the drill list, from the bar or from the ticket. */
function showMenu() {
  document.getElementById("coach").hidden = true;
  menu.show();
}

function renderReceiptButton() {
  const button = document.getElementById("coach-receipt");
  if (button) {
    button.hidden = workout == null || workout.sets.length === 0;
  }
}

function drawMetrics() {
  if (!STATE.metrics.enabled) {
    return;
  }
  const samples = frames.window(STATE.metrics.windowMs);
  let note;
  if (recorder.recording) {
    const mb = (recorder.estimateBytes() / 1048576).toFixed(1);
    note = `● REC ${recorder.durationSeconds.toFixed(0)}s · ${recorder.frames.length} frames · ${recorder.marks.length} marks · ~${mb}MB`;
  } else if (samples.length === 0) {
    note = "no pose — stand in frame";
  } else if (STATE.sourceKind === "file") {
    const video = source.video;
    const at = video.currentTime.toFixed(1);
    const of = Number.isFinite(video.duration)
      ? video.duration.toFixed(1)
      : "?";
    note = `${at}s of ${of}s · click to ${video.paused ? "play" : "pause"} · arrows to step`;
  } else if (STATE.metrics.capture) {
    note = `${(STATE.metrics.windowMs / 1000).toFixed(0)}s window · ${samples.length} frames · R to record`;
  } else if (menu && menu.visible) {
    note = "choose a drill";
  } else if (coach && !coach.running) {
    note = `${(STATE.metrics.windowMs / 1000).toFixed(0)}s window · press Start to count`;
  } else {
    note = `${(STATE.metrics.windowMs / 1000).toFixed(0)}s window · ${samples.length} frames`;
  }
  const scoring = scoringEnabled();
  readout.draw(
    STATE.metrics.view,
    evaluateMetrics(metricsFor(exercise, STATE.metrics.view), samples),
    note,
    scoring ? counterReadout() : null,
    scoring && posture.reps > 0 ? posture.summary() : null
  );
  if (controls && (recorder.recording || controls.countingDown)) {
    controls.render();
  }
}

/**
 * The headline figure under the metric rows, and what it is called.
 *
 * A hold is not a set of reps and must not be labelled as one. The handstand
 * shipped with a big amber `0` under the word REPS through a forty-second hold,
 * with the drill bar beside it correctly reading the clock — two numbers
 * disagreeing about what the drill even measures. The bar had the same problem:
 * a hold's progress is measured against `handstandMinHoldMs`, so it filled in
 * two seconds and then sat full, which is a bar saying nothing for the rest of
 * the hold. A timed drill gets the clock and no bar; see exercises/hold.js.
 */
function counterReadout() {
  if (exercise.timed) {
    return {
      value: `${reps.seconds.toFixed(1)}s`,
      label: "SECONDS",
      progress: null,
    };
  }
  return {
    value: String(reps.count),
    label: "REPS",
    progress: reps.progress,
  };
}

/**
 * The loop, and the one place in the app that must not be allowed to end.
 *
 * `requestAnimationFrame` is only scheduled from inside the callback, so a
 * single frame that throws — one malformed landmark, one metric reading a joint
 * that was not there — does not drop a frame, it stops the app for good: the
 * overlay freezes on the last picture it drew, the counters stop, and nothing
 * recovers short of a reload. That is far too much to lose to one bad frame, so
 * the next frame is booked whatever this one did. The error is reported once
 * rather than every frame at 60Hz, because a loop that keeps running is only an
 * improvement if it does not also fill the console.
 */
function renderPrediction() {
  try {
    renderResult();
  } catch (error) {
    if (!loopFailed) {
      loopFailed = true;
      console.error("frame failed; the loop continues", error);
    }
  }
  rafId = requestAnimationFrame(renderPrediction);
}

function captureMeta() {
  return {
    // The drill the person named, not the one the readout fell back to. An
    // unlabelled take is better than a wrong label: the landmarks are the same
    // either way, so leaving it out says "sort this by hand" where `arm_swing`
    // would have said "measured as an arm swing" about a squat.
    exercise: STATE.metrics.exercise || undefined,
    view: STATE.metrics.view,
    model: STATE.model,
    smoothing: STATE.smoothing,
    camera: { width: source.video.width, height: source.video.height },
  };
}

function saveSession() {
  const filename = downloadSession(recorder, POSE_LANDMARK_NAMES);
  controls.reportSaved(
    filename.name,
    filename.bytes,
    recorder.frames.length,
    recorder.marks.length,
    recorder.fps
  );
}

function applyUrlParams() {
  const urlParams = new URLSearchParams(window.location.search);

  // A phone pays for the bigger model twice: once downloading it over mobile
  // data, then again on every frame. Default to `lite` there; ?model= below
  // still wins for anyone who wants the accuracy.
  if (isMobile()) {
    STATE.model = "lite";
  }

  const model = urlParams.get("model");
  if (model != null && model in MODEL_ASSETS) {
    STATE.model = model;
  }
  if (urlParams.has("render3d")) {
    STATE.render3D = urlParams.get("render3d") !== "0";
  }
  if (urlParams.has("smoothing")) {
    STATE.smoothing.enabled = urlParams.get("smoothing") !== "0";
  }

  const view = urlParams.get("view");
  if (view != null && VIEWS.includes(view)) {
    STATE.metrics.view = view;
  }
  if (urlParams.has("metrics")) {
    STATE.metrics.enabled = urlParams.get("metrics") !== "0";
  }
  if (urlParams.has("record")) {
    STATE.metrics.autoRecord = urlParams.get("record") !== "0";
  }
  // ?source=file reviews a recording instead of opening the camera, which also
  // means the app runs where there is no camera at all.
  const sourceKind = urlParams.get("source");
  if (sourceKind === "file" || sourceKind === "camera") {
    STATE.sourceKind = sourceKind;
  }
  // ?exercise= picks a drill and skips the menu, which is how a recording is
  // reviewed and the only way to drive the whole flow with nobody to tap.
  const exerciseId = urlParams.get("exercise");
  if (exerciseId != null && exerciseById(exerciseId) != null) {
    STATE.metrics.exercise = exerciseId;
  }
  // ?capture=1 swaps the drill bar for the recording bar, which is the tool for
  // capturing fixtures rather than for coaching.
  if (urlParams.has("capture")) {
    STATE.metrics.capture = urlParams.get("capture") !== "0";
  }
  // ?record=1 arms the recorder, and only the recording bar owns one, so it
  // means capture mode whether it was asked for as well or not. Without this
  // the drill bar comes up, nothing there can start a recording, and the first
  // detected pose reaches a toggle on controls that were never built. The two
  // can still be set against each other, and then the explicit one wins: with
  // ?capture=0 the recorder is disarmed rather than the bar swapped back in.
  if (STATE.metrics.autoRecord) {
    if (urlParams.has("capture") && !STATE.metrics.capture) {
      STATE.metrics.autoRecord = false;
    } else {
      STATE.metrics.capture = true;
    }
  }
  // ?intro=0 skips the landing screen, ?intro=1 asks for it on a flow that
  // would otherwise skip it. Unset means `showIntro()` decides.
  if (urlParams.has("intro")) {
    STATE.metrics.intro = urlParams.get("intro") !== "0";
  }
  if (urlParams.has("sound")) {
    STATE.metrics.sound = urlParams.get("sound") !== "0";
  }
  if (urlParams.has("soundcheck")) {
    STATE.metrics.soundcheck = urlParams.get("soundcheck") !== "0";
  }
  if (urlParams.has("posture")) {
    STATE.posture.enabled = urlParams.get("posture") !== "0";
  }
  // ?camsize=640x480 asks the camera for more pixels than a phone gets by
  // default. More pixels is a better head angle and a slower frame rate, and
  // which of those matters is a question for the phone rather than for here.
  const camSize = urlParams.get("camsize");
  if (camSize != null) {
    const wanted = camSize.toLowerCase().replace(/[^0-9x]/g, "");
    const option = Object.keys(VIDEO_SIZE).find(
      (key) => key.toLowerCase().replace(/[^0-9x]/g, "") === wanted
    );
    if (option != null) {
      STATE.camera.sizeOption = option;
      STATE.camera.mobileSizeOption = option;
    }
  }
  // Every posture threshold is overridable, because they are one athlete's reps
  // read by one person and a coach will want to move them without a rebuild.
  for (const key of [
    "tiptoeMin",
    "kneeFlexMax",
    "trunkRockMax",
    "aheadOfKneeMax",
    "neckOffset",
    "neckTolerance",
    "tempoEvenBand",
    "swingSpeedMin",
    "jumpKneeDown",
    "jumpKneeUp",
    "jumpFlightMs",
    "jumpDepthMax",
    "jumpLegsMin",
    "jumpHipsMin",
    "jumpTrunkMax",
    "jumpAirToes",
    "jumpAirMinMs",
    "jumpArmSwingMin",
    "vSitHipDown",
    "vSitHipUp",
    "vSitFoldMax",
    "vSitKneeMin",
    "vSitTrunkRise",
    "handstandInvertMin",
    "handstandLineMin",
    "handstandKneeMin",
    "handstandElbowMin",
    "handstandShoulderMin",
    "handstandMinHoldMs",
    "handstandSettleMs",
    "handstandLeanMin",
    "handstandWallMax",
    // The head band and its facing gate were left out of this list when they
    // were added, so the one criterion on this drill with performed faults
    // behind it was the one that could not be swept from a URL.
    "handstandHeadMax",
    "handstandHeadMin",
    "handstandFacingMin",
  ]) {
    const value = Number(urlParams.get(key.toLowerCase()));
    if (urlParams.has(key.toLowerCase()) && Number.isFinite(value)) {
      STATE.posture[key] = value;
    }
  }
  const countdown = Number(urlParams.get("countdown"));
  if (
    Number.isFinite(countdown) &&
    countdown >= 0 &&
    urlParams.has("countdown")
  ) {
    STATE.metrics.countdownSeconds = Math.min(countdown, 60);
  }
}

/**
 * Whether the landing screen belongs on this load.
 *
 * It is for a person arriving at the app, so the flows that have no person to
 * tap it skip it: capture mode is a tuning errand, `?exercise=` exists to drive
 * the whole pipeline with nobody there, and `?source=file` already stops on a
 * file picker which a headless run drives itself (see "Verifying changes" in
 * AGENTS.md). `?intro=1` forces it back on for any of them, and `?intro=0`
 * turns it off for an ordinary visit.
 */
function showIntro() {
  if (STATE.metrics.intro != null) {
    return STATE.metrics.intro;
  }
  return (
    !STATE.metrics.capture &&
    STATE.metrics.exercise == null &&
    STATE.sourceKind !== "file"
  );
}

/**
 * Without this a failure anywhere in startup is a silent blank page, which is
 * indistinguishable from a slow load and was exactly what made the iOS camera
 * hang so hard to pin down.
 */
/**
 * Last-resort error display. Not a duplicate of the startup panel: `app()`
 * already catches into `startup.fail()`, which is the richer path. This covers
 * the case where the panel itself cannot be built — a missing element, a bad
 * bundle — where the alternative is a silent blank page.
 */
function showFatal(error) {
  console.error("startup failed:", error);
  const panel = document.createElement("div");
  panel.setAttribute("role", "alert");
  panel.style.cssText =
    "position:fixed;inset:0;display:flex;align-items:center;" +
    "justify-content:center;padding:24px;text-align:center;background:#111;" +
    "color:#ff8a80;font:15px system-ui,sans-serif;z-index:20";
  panel.textContent = String((error && error.message) || error);
  document.body.appendChild(panel);
}

async function app() {
  startup = new Startup();
  startup.onRetry(() => window.location.reload());

  try {
    await start();
  } catch (error) {
    startup.fail(error);
  }
}

async function start() {
  applyUrlParams();

  // Before anything is asked of the device. The camera prompt used to be the
  // first thing a new visitor saw, over a page that had not yet said what this
  // is, and a permission dialog nobody has been given a reason for gets
  // blocked — after which only browser settings can undo it. So the landing
  // screen is shown first and `Camera.setup` waits for the press.
  if (showIntro()) {
    await new Intro().wait();
  }

  if (STATE.sourceKind === "file") {
    source = await pickVideoFile();
  } else {
    startup.stage(
      "Starting camera\u2026",
      "Allow camera access when prompted."
    );
    source = cameraSource(await Camera.setup(STATE.camera));
  }
  landmarker = await createLandmarker();

  normalizedSmoother = new LandmarkSmoother(STATE.smoothing.normalized);
  worldSmoother = new LandmarkSmoother(STATE.smoothing.world);

  const canvas = document.getElementById("output");
  canvas.width = source.video.width;
  canvas.height = source.video.height;
  renderer = new RendererCanvas2d(canvas, { mirror: source.mirrored });
  readout = new Readout(canvas);
  frames = new FrameBuffer(STATE.metrics.bufferFrames);
  recorder = new SessionRecorder();
  posture = new PostureTally();
  // The ticket is the day's, not the page's: a phone that locked between sets
  // and came back is the same workout. See metrics/history.js.
  history = new WorkoutStore();
  const restored = history.load(exerciseById);
  workout = restored.workout;
  earned = restored.earned;
  // Levels survive the page, so they are read before anything is drawn. On a
  // device where storage is blocked this is simply empty and the drills still
  // run; see metrics/progress.js.
  progress = new Progress();

  // Two different jobs, so two different bars, and only one at a time. The
  // recorder's controls — "Mark rep" above all, which exists only to label
  // ground truth for tuning — are a tool for whoever is working on the detector,
  // not something to hand an athlete mid-drill.
  if (STATE.metrics.capture) {
    document.getElementById("coach").hidden = true;
    document.getElementById("controls").hidden = false;
    controls = new RecorderControls(recorder, {
      onStart: captureMeta,
      onStop: saveSession,
      countdownSeconds: STATE.metrics.countdownSeconds,
    });
    // Capture skips the menu — it is a tuning errand, not a drill — but it is
    // not the arm swing's errand any more: `?capture=1&exercise=squat` frames
    // the readout for the drill being filmed, and names the drill in the file
    // and in its filename. The landmarks written are the same either way — they
    // are raw and belong to no exercise — but a take scored against the wrong
    // drill's thresholds reads as a plausible number for a movement nobody did,
    // so which drill it was has to travel with it.
    selectExercise(
      exerciseById(STATE.metrics.exercise) || exerciseById(DEFAULT_EXERCISE)
    );
    document.getElementById("coach").hidden = true;
  } else {
    coach = new CoachControls({
      onStart: startSet,
      onStop: endSet,
      countdownSeconds: STATE.metrics.countdownSeconds,
      sound: STATE.metrics.sound,
      soundcheck: STATE.metrics.soundcheck,
      // Both the menu and the receipt sit over this bar and own the keyboard
      // while they are up; a window keydown listener still fires underneath.
      canStart: () => !menu.visible && !receipt.visible,
      onMenu: showMenu,
    });
    menu = new ExerciseMenu(EXERCISES, {
      onPick: selectExercise,
      progress,
    });
    receipt = new Receipt({
      onAgain: () => coach.setExercise(exercise),
      onMenu: showMenu,
    });
    document.getElementById("coach-receipt").addEventListener("click", () => {
      if (workout.sets.length > 0) {
        receipt.show(workout, earned);
      }
    });

    const chosen = exerciseById(STATE.metrics.exercise);
    if (chosen) {
      // `?exercise=` goes straight to a drill, which is how a recording is
      // reviewed and how the pipeline is driven with no one to tap the menu.
      selectExercise(chosen);
    } else {
      menu.show();
    }
  }

  startup.done();
  if (typeof source.play === "function") {
    bindReviewControls(source);
    source.play();
  }
  renderPrediction();
}

/**
 * Click to pause, arrow keys to step. Reviewing is the point of file mode, and
 * it is not reviewing if the only thing it can do is play once from the start.
 *
 * Left alone: Space and R, which the recorder controls already own.
 */
function bindReviewControls(file) {
  const canvas = document.getElementById("output");
  canvas.style.cursor = "pointer";
  canvas.addEventListener("click", () => {
    if (file.video.paused) {
      file.play();
    } else {
      file.video.pause();
    }
  });
  window.addEventListener("keydown", (event) => {
    const step = event.shiftKey ? 0.1 : 1;
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      file.video.currentTime = Math.max(0, file.video.currentTime - step);
    } else if (event.key === "ArrowRight") {
      event.preventDefault();
      file.video.currentTime = Math.min(
        file.video.duration || 0,
        file.video.currentTime + step
      );
    }
  });
}

/**
 * Waits for the athlete to choose a file. A file cannot be opened without a
 * gesture, so this is a prompt rather than a load, and the startup panel is
 * where it belongs: it is the one thing standing between here and a running app.
 */
function pickVideoFile() {
  const input = document.getElementById("video-file");
  startup.stage(
    "Choose a video\u2026",
    "Nothing is uploaded; it is read on this device."
  );
  input.hidden = false;
  return new Promise((resolve, reject) => {
    input.addEventListener(
      "change",
      () => {
        const file = input.files && input.files[0];
        input.hidden = true;
        if (!file) {
          reject(new Error("No video chosen."));
          return;
        }
        startup.stage("Reading the video\u2026", file.name);
        VideoFile.setup(file).then(resolve, reject);
      },
      { once: true }
    );
  });
}

app().catch(showFatal);
